// src/components/cash/SavingsTab.js
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// Reuse custom components from BankPay.js
const Button = ({ children, onClick, disabled, style }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: 'transparent',
      border: '2px solid #FFD700',
      color: '#FFD700',
      padding: '10px 20px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontWeight: 'bold',
      width: 'auto',
      ...(disabled && { opacity: 0.5, cursor: 'not-allowed' }),
      ...style,
    }}
  >
    {children}
  </button>
);

const Input = ({ placeholder, value, onChange, type, min, step, style }) => (
  <input
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    type={type}
    min={min}
    step={step}
    style={{
      width: '100%',
      padding: '10px',
      border: '1px solid #FFD700',
      borderRadius: '5px',
      background: '#111',
      color: '#FFD700',
      ...style,
    }}
  />
);

export const SavingsTab = ({ userAddress, contract, contractData, loading, setLoading, setError }) => {
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [savingsData, setSavingsData] = useState({
    balance: 0,
    accruedInterest: 0,
    isActive: false,
    lastUpdateTime: 0
  });
  const [transactionHistory, setTransactionHistory] = useState([]);

  // Fetch user's savings data from contract
  const fetchSavingsData = async () => {
    if (!contract || !userAddress) return;

    setLoading(true);
    try {
      const [savingsAccount] = await contract.getSavingsAccount(userAddress);

      setSavingsData({
        balance: parseFloat(ethers.formatUnits(savingsAccount.balance, 6)),
        accruedInterest: parseFloat(ethers.formatUnits(savingsAccount.accruedInterest, 6)),
        isActive: savingsAccount.isActive,
        lastUpdateTime: Number(savingsAccount.lastUpdateTime)
      });

      setError(null);
    } catch (error) {
      console.error('Failed to fetch savings data from contract:', error);
      setError('Failed to load savings data from contract');
    } finally {
      setLoading(false);
    }
  };

  // Setup event listeners for real-time updates
  useEffect(() => {
    if (!contract) return;

    const setupEventListeners = () => {
      try {
        // Deposit event
        contract.on('Deposit', (user, amount, timestamp) => {
          if (user.toLowerCase() === userAddress.toLowerCase()) {
            console.log('Deposit event:', {
              user,
              amount: ethers.formatUnits(amount, 6),
              timestamp: new Date(Number(timestamp) * 1000).toLocaleString()
            });

            // Add to transaction history
            setTransactionHistory(prev => [{
              type: 'Deposit',
              amount: ethers.formatUnits(amount, 6),
              timestamp: Number(timestamp),
              hash: 'event' // Placeholder for actual tx hash
            }, ...prev]);

            // Refresh savings data
            fetchSavingsData();
          }
        });

        // Withdrawal event
        contract.on('Withdrawal', (user, amount, timestamp) => {
          if (user.toLowerCase() === userAddress.toLowerCase()) {
            console.log('Withdrawal event:', {
              user,
              amount: ethers.formatUnits(amount, 6),
              timestamp: new Date(Number(timestamp) * 1000).toLocaleString()
            });

            // Add to transaction history
            setTransactionHistory(prev => [{
              type: 'Withdrawal',
              amount: ethers.formatUnits(amount, 6),
              timestamp: Number(timestamp),
              hash: 'event' // Placeholder for actual tx hash
            }, ...prev]);

            // Refresh savings data
            fetchSavingsData();
          }
        });

        // Emergency withdrawal event
        contract.on('EmergencyWithdrawal', (user, amount) => {
          if (user.toLowerCase() === userAddress.toLowerCase()) {
            console.log('Emergency withdrawal event:', {
              user,
              amount: ethers.formatUnits(amount, 6)
            });

            // Add to transaction history
            setTransactionHistory(prev => [{
              type: 'Emergency Withdrawal',
              amount: ethers.formatUnits(amount, 6),
              timestamp: Math.floor(Date.now() / 1000),
              hash: 'event'
            }, ...prev]);

            // Refresh savings data
            fetchSavingsData();
          }
        });

      } catch (error) {
        console.error('Error setting up event listeners:', error);
      }
    };

    setupEventListeners();

    // Cleanup event listeners on unmount
    return () => {
      if (contract) {
        contract.removeAllListeners('Deposit');
        contract.removeAllListeners('Withdrawal');
        contract.removeAllListeners('EmergencyWithdrawal');
      }
    };
  }, [contract, userAddress]);

  useEffect(() => {
    if (userAddress && contract) {
      fetchSavingsData();
    }
  }, [userAddress, contract]);

  // Handle deposit
  const handleDeposit = async () => {
    if (!contract || !depositAmount || parseFloat(depositAmount) <= 0) {
      setError('Please enter a valid deposit amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const amountInUnits = ethers.parseUnits(depositAmount, 6);

      // First, approve USDC for the contract
      const usdcAddress = await contract.USDC();
      const usdcAbi = ['function approve(address spender, uint256 amount) returns (bool)'];
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const usdcContract = new ethers.Contract(usdcAddress, usdcAbi, signer);

      // Approve USDC
      const approveTx = await usdcContract.approve(contract.target, amountInUnits);
      await approveTx.wait();

      // Then deposit
      const depositTx = await contract.deposit(amountInUnits);
      alert(`✅ Deposit submitted! Transaction: ${depositTx.hash}`);

      await depositTx.wait();
      alert(`✅ Deposit confirmed!`);

      // Refresh data
      await fetchSavingsData();
      setDepositAmount('');

    } catch (error) {
      console.error('Deposit error:', error);
      setError(`Deposit failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle withdrawal
  const handleWithdraw = async () => {
    if (!contract || !withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setError('Please enter a valid withdrawal amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const amountInUnits = ethers.parseUnits(withdrawAmount, 6);

      const tx = await contract.withdraw(amountInUnits);
      alert(`✅ Withdrawal submitted! Transaction: ${tx.hash}`);

      await tx.wait();
      alert(`✅ Withdrawal confirmed!`);

      // Refresh data
      await fetchSavingsData();
      setWithdrawAmount('');

    } catch (error) {
      console.error('Withdrawal error:', error);
      setError(`Withdrawal failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle emergency withdrawal
  const handleEmergencyWithdraw = async () => {
    if (!contract) {
      setError('Contract not initialized');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tx = await contract.emergencyWithdraw();
      alert(`✅ Emergency withdrawal submitted! Transaction: ${tx.hash}`);

      await tx.wait();
      alert(`✅ Emergency withdrawal confirmed!`);

      // Refresh data
      await fetchSavingsData();

    } catch (error) {
      console.error('Emergency withdrawal error:', error);
      setError(`Emergency withdrawal failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate projected interest
  const calculateProjectedInterest = (days = 365) => {
    const principal = savingsData.balance;
    const apy = parseFloat(contractData.savingsAPY || 0) / 100; // Convert to decimal
    const dailyRate = apy / 365;
    return principal * dailyRate * days;
  };

  const totalBalance = savingsData.balance + savingsData.accruedInterest;
  const projectedAnnualInterest = calculateProjectedInterest(365);
  const projectedMonthlyInterest = calculateProjectedInterest(30);

  return (
    <div className="max-w-2xl mx-auto bg-[#111] rounded-lg shadow-md p-6" style={{ color: '#FFD700' }}>
      {loading && <div className="text-center" style={{ color: '#FFD700', marginBottom: '20px' }}>Loading contract data...</div>}

      <h2 className="text-2xl font-bold mb-4">Savings Overview</h2>

      {/* Savings Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        padding: '15px',
        background: 'rgba(255, 215, 0, 0.1)',
        border: '1px solid #FFD700',
        borderRadius: '5px',
        marginBottom: '20px'
      }}>
        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Total Balance:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${totalBalance.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Principal:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${savingsData.balance.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Accrued Interest:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${savingsData.accruedInterest.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Current APY:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>{contractData.savingsAPY}%</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Projected Monthly:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${projectedMonthlyInterest.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Projected Annual:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${projectedAnnualInterest.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Status:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>
          <span style={{
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            background: savingsData.isActive ? '#00FF00' : '#FF0000',
            color: '#111',
          }}>
            {savingsData.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Last Updated:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '12px' }}>
          {savingsData.lastUpdateTime ? new Date(savingsData.lastUpdateTime * 1000).toLocaleDateString() : 'Never'}
        </div>
      </div>

      {/* Deposit Section */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Make Deposit</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label className="block text-sm mb-2">Deposit Amount (USDC)</label>
            <Input
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder="Enter USDC amount"
              min="0"
              step="0.01"
            />
          </div>
          <Button onClick={handleDeposit} disabled={loading || !depositAmount}>
            {loading ? 'Processing...' : 'Deposit USDC'}
          </Button>
        </div>
        {depositAmount && (
          <div style={{ marginTop: '15px', padding: '15px', border: '1px solid #FFD700', borderRadius: '8px', background: '#111' }}>
            <p className="text-sm">Fee: {(parseFloat(depositAmount || 0) * parseFloat(contractData.feePercent || 0) / 100).toFixed(4)} USDC</p>
            <p className="text-sm text-green-600 mt-1">Projected APY: {contractData.savingsAPY}%</p>
          </div>
        )}
      </div>

      {/* Withdrawal Section */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Withdraw Funds</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label className="block text-sm mb-2">Withdrawal Amount (USDC)</label>
            <Input
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              placeholder="Enter withdrawal amount"
              min="0"
              step="0.01"
              max={totalBalance}
            />
          </div>
          <Button onClick={handleWithdraw} disabled={loading || !withdrawAmount || parseFloat(withdrawAmount) > totalBalance}>
            {loading ? 'Processing...' : 'Withdraw USDC'}
          </Button>
        </div>
        {withdrawAmount && (
          <div style={{ marginTop: '15px', padding: '15px', border: '1px solid #FFD700', borderRadius: '8px', background: '#111' }}>
            <p className="text-sm">Fee: {(parseFloat(withdrawAmount || 0) * parseFloat(contractData.feePercent || 0) / 100).toFixed(4)} USDC</p>
            <p className="text-sm text-orange-600 mt-1">Available: ${totalBalance.toFixed(2)} USDC</p>
          </div>
        )}
      </div>

      {/* Emergency Withdrawal Section */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Emergency Withdrawal</h3>
        <p className="text-sm mb-4" style={{ color: '#ccc' }}>
          Use this only in emergency situations to withdraw all funds immediately. This may incur penalties or loss of accrued interest.
        </p>
        <Button onClick={handleEmergencyWithdraw} disabled={loading || totalBalance <= 0} style={{ background: 'rgba(255, 0, 0, 0.2)' }}>
          {loading ? 'Processing...' : 'Emergency Withdraw All'}
        </Button>
      </div>

      {/* Transaction History */}
      <div>
        <h3 className="text-xl font-bold mb-4">Transaction History</h3>
        {transactionHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No transactions yet. Deposits and withdrawals will appear here.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #FFD700' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Amount</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactionHistory.map((tx, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #FFD700' }}>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: tx.type === 'Deposit' ? '#00FF00' :
                                   tx.type === 'Withdrawal' ? '#FFA500' : '#FF0000',
                        color: '#111',
                      }}>
                        {tx.type}
                      </span>
                    </td>
                    <td style={{ padding: '10px', fontWeight: 'bold' }}>
                      {tx.type === 'Deposit' ? '+' : '-'}${parseFloat(tx.amount).toFixed(2)}
                    </td>
                    <td style={{ padding: '10px', color: '#ccc', fontSize: '12px' }}>
                      {new Date(tx.timestamp * 1000).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Savings Information */}
      <div style={{
        marginTop: '30px',
        padding: '15px',
        border: '1px solid #FFD700',
        borderRadius: '8px',
        background: 'rgba(255, 215, 0, 0.1)',
        fontSize: '14px'
      }}>
        <h4 className="font-bold mb-2">Savings Features:</h4>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '5px' }}>✓ Automatic interest compounding</li>
          <li style={{ marginBottom: '5px' }}>✓ {contractData.savingsAPY}% APY returns</li>
          <li style={{ marginBottom: '5px' }}>✓ Secure smart contract storage</li>
          <li style={{ marginBottom: '5px' }}>✓ Real-time transaction tracking</li>
          <li style={{ marginBottom: '5px' }}>✓ {contractData.feePercent}% fee on transactions</li>
        </ul>
      </div>
    </div>
  );
};
